import { useGuestSessionStore } from '@/state/store/guestSession';

// Frontend service that talks to the Node backend's guest payment endpoints.
// Uses fetch directly (NOT the existing `clients` HTTP wrapper) because:
//   - The backend lives at VITE_BACKEND_API_URL, not the UDS gateway base.
//   - Auth is via the X-Guest-Session-Id header, not a Bearer token.

const BACKEND_URL =
  (import.meta.env.VITE_BACKEND_API_URL as string | undefined)?.replace(/\/$/, '') || '';

export interface PaymentSession {
  id: string;
  sessionData: string;
  clientKey: string;
  environment: 'TEST' | 'LIVE';
  amount: { value: number; currency: string };
  countryCode: string;
}

export interface PaymentStatus {
  isPaid: boolean;
  paidAt: string | null;
}

const guestSessionHeader = (): Record<string, string> => {
  const id = useGuestSessionStore.getState().guestSessionId;
  if (!id) {
    throw new Error('No guest session — call ensureGuestSession() first');
  }
  return { 'X-Guest-Session-Id': id };
};

const handleEnvelope = async <T>(res: Response, errorPrefix: string): Promise<T> => {
  if (!res.ok) {
    let detail = '';
    try {
      const err = (await res.json()) as { message?: string };
      detail = err?.message ?? '';
    } catch {
      // ignore body parse errors
    }
    throw new Error(`${errorPrefix} (${res.status}) ${detail}`.trim());
  }
  const payload = (await res.json()) as { success: boolean; data: T };
  if (!payload?.success) {
    throw new Error(`${errorPrefix}: response not successful`);
  }
  return payload.data;
};

export const createPaymentSession = async (documentId: string): Promise<PaymentSession> => {
  if (!BACKEND_URL) {
    throw new Error('VITE_BACKEND_API_URL is not configured');
  }
  const res = await fetch(`${BACKEND_URL}/guest/payments/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...guestSessionHeader(),
    },
    body: JSON.stringify({ documentId }),
  });
  return handleEnvelope<PaymentSession>(res, 'Create payment session failed');
};

export const getPaymentStatus = async (documentId: string): Promise<PaymentStatus> => {
  if (!BACKEND_URL) {
    throw new Error('VITE_BACKEND_API_URL is not configured');
  }
  const res = await fetch(
    `${BACKEND_URL}/guest/payments/status/${encodeURIComponent(documentId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...guestSessionHeader(),
      },
    }
  );
  return handleEnvelope<PaymentStatus>(res, 'Fetch payment status failed');
};
